import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useNotifications } from '@/contexts/NotificationsContext';
import { pushApi } from '@/lib/api';
import AppNavbar from '@/components/app/AppNavbar';
import MobileBottomNav from '@/components/app/MobileBottomNav';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Bell, UserPlus, ThumbsUp, Smile, BellRing, MessageSquare } from 'lucide-react';
import TimeAgo from '@/components/app/TimeAgo';

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
}

function notificationMeta(n) {
  switch (n.type) {
    case 'follow':
      return {
        icon: <UserPlus className="w-3.5 h-3.5 text-terra" />,
        text: 'started following you',
        href: n.actor ? `/profile/${n.actor.username}` : null,
      };
    case 'review_helpful':
      return {
        icon: <ThumbsUp className="w-3.5 h-3.5 text-terra" />,
        text: n.book ? `found your review of "${n.book.title}" helpful` : 'found your review helpful',
        href: n.book ? `/books/${n.book.id}` : null,
      };
    case 'reaction_like':
      return {
        icon: <Smile className="w-3.5 h-3.5 text-terra" />,
        text: n.book ? `liked your reaction on "${n.book.title}"` : 'liked your reaction',
        href: n.book ? `/books/${n.book.id}` : null,
      };
    case 'discussion_reply':
      return {
        icon: <MessageSquare className="w-3.5 h-3.5 text-terra" />,
        text: n.book ? `replied to your discussion on "${n.book.title}"` : 'replied to your discussion',
        href: n.book ? `/books/${n.book.id}` : null,
      };
    default:
      return { icon: <Bell className="w-3.5 h-3.5 text-terra" />, text: 'sent you a notification', href: null };
  }
}

function PushPermissionBanner() {
  const [permission, setPermission] = useState(
    'Notification' in window ? Notification.permission : 'unsupported'
  );
  const [subscribing, setSubscribing] = useState(false);

  if (!process.env.REACT_APP_VAPID_PUBLIC_KEY) return null;
  if (permission !== 'default') return null;

  const handleEnable = async () => {
    setSubscribing(true);
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result === 'granted') await pushApi.subscribe();
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <div className="bg-terra/5 border border-terra/20 rounded-xl p-4 mb-6 flex items-start gap-3">
      <BellRing className="w-5 h-5 text-terra flex-shrink-0 mt-0.5" />
      <div className="flex-1">
        <p className="text-sm font-semibold text-ink">Get push notifications</p>
        <p className="text-xs text-ink-muted mt-0.5">Know instantly when someone follows you or reacts to your reviews.</p>
      </div>
      <Button
        size="sm"
        onClick={handleEnable}
        disabled={subscribing}
        className="bg-terra hover:bg-terra/90 text-cream text-xs flex-shrink-0"
      >
        {subscribing ? 'Enabling…' : 'Enable'}
      </Button>
    </div>
  );
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const { notifications, unreadCount, loading, markRead, markAllRead } = useNotifications();

  const handleClick = async (n) => {
    const { href } = notificationMeta(n);
    if (!n.read) await markRead([n.id]);
    if (href) navigate(href);
  };

  return (
    <div className="min-h-screen bg-cream pb-20 md:pb-0">
      <AppNavbar />

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-terra" />
            <h1 className="font-heading text-2xl text-ink">Notifications</h1>
            {unreadCount > 0 && (
              <span className="bg-terra text-cream text-xs font-bold rounded-full px-2 py-0.5">
                {unreadCount}
              </span>
            )}
          </div>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={markAllRead}
              className="text-terra hover:text-terra/80 text-xs"
            >
              Mark all read
            </Button>
          )}
        </div>

        <PushPermissionBanner />

        {loading ? (
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="bg-cream border border-cream-dark rounded-xl p-4 flex items-start gap-3">
                <Skeleton className="w-10 h-10 rounded-full bg-cream-dark flex-shrink-0" />
                <div className="space-y-2 flex-1">
                  <Skeleton className="h-3.5 w-full bg-cream-dark" />
                  <Skeleton className="h-3 w-24 bg-cream-dark" />
                </div>
              </div>
            ))}
          </div>
        ) : notifications.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-20"
          >
            <Bell className="w-12 h-12 text-ink-muted mx-auto mb-4 opacity-40" />
            <h3 className="font-heading text-xl text-ink mb-2">All quiet here</h3>
            <p className="text-sm text-ink-muted max-w-xs mx-auto">
              When people follow you or interact with your reviews and reactions, you'll see it here.
            </p>
          </motion.div>
        ) : (
          <div className="space-y-2">
            {notifications.map((n) => {
              const { icon, text } = notificationMeta(n);
              return (
                <motion.button
                  key={n.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  onClick={() => handleClick(n)}
                  className={`w-full flex items-start gap-3 p-4 rounded-xl border text-left transition-colors hover:border-terra/30 ${
                    n.read
                      ? 'bg-cream border-cream-dark'
                      : 'bg-terra/5 border-terra/20'
                  }`}
                >
                  <div className="relative flex-shrink-0">
                    <Avatar className="w-10 h-10">
                      <AvatarImage src={n.actor?.avatar_url} alt={n.actor?.display_name} />
                      <AvatarFallback className="bg-terra text-cream text-sm font-semibold">
                        {getInitials(n.actor?.display_name || n.actor?.username)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-cream border border-cream-dark flex items-center justify-center">
                      {icon}
                    </span>
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-ink leading-snug">
                      <span className="font-semibold">{n.actor?.display_name || n.actor?.username || 'Someone'}</span>
                      {' '}{text}
                    </p>
                    <TimeAgo date={n.created_at} className="text-xs text-ink-muted mt-1 block" />
                  </div>

                  {!n.read && (
                    <span className="w-2 h-2 rounded-full bg-terra flex-shrink-0 mt-1.5" />
                  )}
                </motion.button>
              );
            })}
          </div>
        )}
      </div>

      <MobileBottomNav />
    </div>
  );
}
